import React, { PropsWithChildren, RefObject, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hydrateCartFromStorage } from '@r1-frontend/entities/Cart/store/thunk';
import type { TCartThunkDispatch } from '@r1-frontend/entities/Cart/store/types';
import ChatContext from '@r1-frontend/entities/Chat';
import { CDN_API_ENDPOINT } from '@r1-frontend/entities/Layout/_deps/constants';
import { getABNVariant } from '@r1-frontend/entities/Layout/_deps/helpers/abn-tests';
import CitySelectContainer from '@r1-frontend/entities/Layout/CitySelectContainer';
import Header from '@r1-frontend/entities/Layout/Header';
import { selectCsrf } from '@r1-frontend/entities/stores/auth/selectors/selectCsrf';

import BP42CallbackPopup from '@r1-frontend/bp42';
import BP42Context from '@r1-frontend/bp42/BP42Context';
import { SurveyButton, SurveyContext, SurveyDialog } from '@r1-frontend/survey';

import { GlobalStyle } from '@r1-frontend/ui-react/components/GlobalStyles';
import UIKitGlobalStyle from '@r1-frontend/ui-kit/themes/UIKitGlobalStyle';

import { Footer } from '../Footer';

import * as ST from './styled';

interface IApp {
    withMenu: boolean,
    withChat: boolean,
    withFooter: boolean,
    withBottomSticky?: RefObject<HTMLDivElement>,
    bgColor?: string,
    withEmailWidget?: boolean,
    emailWidgetText?: string,
}

const App = ({
    withMenu,
    withChat,
    withFooter,
    bgColor,
    withEmailWidget,
    withBottomSticky,
    emailWidgetText,
    children,
}: PropsWithChildren<IApp>) => {
    const dispatch = useDispatch<TCartThunkDispatch>();
    const csrf = useSelector(selectCsrf);

    const showFeedback = useMemo(() => getABNVariant('feedback') === 'show_feedback', []);

    useEffect(() => {
        dispatch(hydrateCartFromStorage());
    }, [dispatch]);

    return (
        <ST.AppContainer bgColor={bgColor} ref={withBottomSticky}>
            <SurveyContext>
                <BP42Context csrf={csrf}>
                    <ChatContext enable={withChat}>
                        <GlobalStyle endpoint={CDN_API_ENDPOINT}/>
                        <ST.GlobalStyle/>
                        <UIKitGlobalStyle endpoint={CDN_API_ENDPOINT}/>
                        {withMenu && <Header data-test='main-menu'/>}
                        <ST.Content>
                            {children}

                            {withFooter && <Footer
                                withEmailWidget={withEmailWidget}
                                emailWidgetText={emailWidgetText}
                                withChat={withChat}
                            />}
                        </ST.Content>
                        <CitySelectContainer/>
                        <BP42CallbackPopup/>
                        {showFeedback && (
                            <SurveyButton/>
                        )}
                        <SurveyDialog/>
                    </ChatContext>
                </BP42Context>
            </SurveyContext>
        </ST.AppContainer>
    );
};

export default App;
